import { useId, type ReactNode, type Ref } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import useToggle from 'hooks/useToggle'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'

const AssistantThoughtsWrapper = styled.div`
  width: 100%;
`

const AssistantThoughtsToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  padding: 0;
  border: 0;
  background: transparent;
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  cursor: pointer;
  font-family: ${props => props.theme.font.primary.family};
  text-align: start;
  text-decoration: none;

  &:hover,
  &:focus-visible,
  &:active {
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
    text-decoration: none;
  }
`

const AssistantThoughtsContent = styled.div`
  margin-block-start: ${rem(12)};
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

  > * + * {
    margin-block-start: ${rem(12)};
  }

  p {
    white-space: pre-line;
  }

  .assistant-step-text,
  .assistant-step-text * {
    color: inherit;
  }
`

interface Props {
  toggleRef?: Ref<HTMLButtonElement>
  generationStartedAt?: string
  generationFinishedAt?: string
  children: ReactNode
}

function AssistantThoughts({ generationStartedAt, generationFinishedAt, toggleRef, children }: Props) {
  const intl = useIntl()
  const contentId = useId()
  const [isExpanded, toggle] = useToggle()
  const started = generationStartedAt ? new Date(generationStartedAt).getTime() : NaN
  const finished = generationFinishedAt ? new Date(generationFinishedAt).getTime() : NaN
  let assistantThoughtDuration: string | undefined
  if (Number.isFinite(started) && Number.isFinite(finished) && finished > started) {
    const totalSeconds = Math.max(1, Math.round((finished - started) / 1000))
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    const units: Array<string> = []
    if (minutes) units.push(intl.formatNumber(minutes, { style: 'unit', unit: 'minute', unitDisplay: 'narrow' }))
    if (seconds) units.push(intl.formatNumber(seconds, { style: 'unit', unit: 'second', unitDisplay: 'narrow' }))
    assistantThoughtDuration = units.join(' ')
  }
  let assistantThoughtLabel: string
  if (assistantThoughtDuration) {
    assistantThoughtLabel = intl.formatMessage({
      id: 'KRnDBtMrac6SJhkdByC0',
      defaultMessage: 'Planned for {duration}',
      description: 'Collapsed label for interim AI assistant reasoning and progress text in trip planner chat',
    }, { duration: assistantThoughtDuration })
  } else {
    assistantThoughtLabel = intl.formatMessage({
      id: 'T5JEgkBob6bs1U3L2Oxl',
      defaultMessage: 'Thoughts',
      description: 'Collapsed label for interim AI assistant reasoning and progress text in trip planner chat when duration is unavailable',
    })
  }

  return (
    <AssistantThoughtsWrapper className="assistant-thoughts">
      <AssistantThoughtsToggle ref={toggleRef} type="button" aria-expanded={isExpanded} aria-controls={contentId} onClick={toggle}>
        <BodyText as="span" variant="medium" weight="normal">{assistantThoughtLabel}</BodyText>
        <DropdownChevron open={isExpanded} size="XS" />
      </AssistantThoughtsToggle>
      <AssistantThoughtsContent id={contentId} hidden={!isExpanded}>{isExpanded && children}</AssistantThoughtsContent>
    </AssistantThoughtsWrapper>
  )
}

export default AssistantThoughts
